import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { IAlert } from 'src/app/core/dto';
import { AlertDataService } from 'src/app/main/shared/services/alert-data.service';

/** localStorage: alert id → the local day (`YYYY-MM-DD`) it was dismissed. */
export const DISMISSED_KEY = 'alertBannerDismissed';

/** The action link per alert type; types without one show no link. */
const LINK_LABEL_KEYS: Record<string, string> = {
  Licensing: 'alertBannerViewLicense',
};

/** `YYYY-MM-DD` in local time. A dismissal lasts until this changes. */
export function localDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function readDismissed(): Record<string, string> {
  try {
    const value = JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '{}');
    return value && typeof value === 'object' ? value : {};
  } catch {
    return {};
  }
}

function writeDismissed(dismissed: Record<string, string>): void {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
  } catch {
    // Private mode or storage full: the dismissal lasts for this page only.
  }
}

/**
 * The banner under the Steel header: the first alert the server flags `showInBanner` (so far only
 * the license expiring within 7 days). Dismissing hides that alert until the next calendar day.
 */
@Component({
  selector: 'app-steel-alert-banner',
  templateUrl: './steel-alert-banner.component.html',
  styleUrls: ['./steel-alert-banner.component.scss'],
  imports: [RouterLink, MatTooltipModule, TranslateModule],
})
export class SteelAlertBannerComponent {
  private alertService = inject(AlertDataService);

  private readonly alerts = toSignal(this.alertService.alerts$, { initialValue: [] as IAlert[] });
  private readonly dismissed = signal(readDismissed());

  /** Checked every minute, so a dismissed banner comes back after midnight without a reload. */
  readonly today = signal(localDay(new Date()));

  readonly alert = computed(() => {
    const today = this.today();
    const dismissed = this.dismissed();
    return this.alerts().find((a) => a.showInBanner && dismissed[a.id] !== today) ?? null;
  });

  readonly linkLabelKey = computed(() => LINK_LABEL_KEYS[this.alert()?.alertType] ?? null);
  readonly link = computed(() => {
    const sref = this.alert()?.sref;
    return sref?.startsWith('/') ? sref : null;
  });

  constructor() {
    const timer = setInterval(() => this.today.set(localDay(new Date())), 60_000);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }

  dismiss(alert: IAlert): void {
    const today = this.today();
    // Only today's dismissals still matter; drop the rest so the entry doesn't grow.
    const kept = Object.fromEntries(
      Object.entries(this.dismissed()).filter(([, day]) => day === today)
    );
    const next = { ...kept, [alert.id]: today };
    writeDismissed(next);
    this.dismissed.set(next);
  }
}
